import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  CONTAINER_MESSAGE_TEMPLATE_KEYS,
  type ContainerMessageTemplateKey,
} from "../../types/containerMessages";

/**
 * The footer of a container's conversation: five taps and a text box.
 *
 * The chips are the point of the driver's half of this feature. A driver
 * messages from a cab — moving, in gloves, at a gate, often one-handed — and
 * the five things they need to say are known in advance, so they are buttons
 * rather than sentences. Speed is only half of it: a tapped message carries a
 * stable key, which makes "how often are we told about a problem on site" a
 * query instead of a text search over whatever people happened to type.
 *
 * The row scrolls horizontally rather than wrapping. Wrapping would push the
 * text box down a line on a narrow phone and change the height of the footer
 * depending on the language it is in — and the footer is the one part of this
 * screen whose position must not move, because it is what a thumb reaches for
 * without looking.
 */

interface Props {
  onSend: (
    data:
      | { body: string; templateKey?: never }
      | { templateKey: ContainerMessageTemplateKey; body?: never },
  ) => Promise<boolean> | boolean;
  sending?: boolean;
  disabled?: boolean;
  /** Said in place of the composer when the thread is closed to new messages. */
  disabledNotice?: string;
  /** The five taps. Off for the office, whose phrasing they are not. */
  showQuick?: boolean;
  placeholder?: string;
  /**
   * Rendered for a thumb rather than a mouse: 44px send button. On for the
   * driver's tab only — the office panel sits in a page with its own chrome
   * and should be left exactly as it was.
   */
  touch?: boolean;
}

export function ContainerMessageComposer({
  onSend,
  sending = false,
  disabled = false,
  disabledNotice,
  showQuick = false,
  placeholder,
  touch = false,
}: Props) {
  const { t } = useTranslation("containerOrders");
  const [text, setText] = useState("");

  if (disabled) {
    return (
      <div className="border-t border-[#E4E2DA] bg-white px-3 py-3 text-center text-[12.5px] text-[#8A9590]">
        {disabledNotice ?? t("messages.closed")}
      </div>
    );
  }

  const submitText = async () => {
    const body = text.trim();
    if (!body || sending) return;
    // Cleared optimistically: a driver who has pressed send has moved on, and
    // a box that empties only once the network answers is one they retype into
    // on a bad connection. A failure surfaces as a toast from the caller, with
    // the text still recoverable from the thread's error state.
    setText("");
    const ok = await onSend({ body });
    if (!ok) setText(body);
  };

  return (
    <div className="border-t border-[#E4E2DA] bg-white">
      {showQuick && (
        // No scrollbar: Android draws one under the chips. The half-shown
        // last chip already says the row swipes.
        <div className="flex gap-2 overflow-x-auto px-3 pb-2 pt-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CONTAINER_MESSAGE_TEMPLATE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              disabled={sending}
              onClick={() => onSend({ templateKey: key })}
              className="shrink-0 whitespace-nowrap rounded-full border border-[#EBBE86] px-3 py-1.5 text-[12.5px] text-[#8A5A28] transition-colors hover:bg-[#FDF6EE] disabled:opacity-50"
            >
              {t(`messages.templates.${key}`)}
            </button>
          ))}
        </div>
      )}

      {/* No home-indicator padding: on the driver's screen this row sits on the
          tab bar, which clears the indicator itself, and while the keyboard is
          up the indicator is under the keyboard. `pb-safe` here only opened a
          gap above the bar. */}
      <div className="flex items-center gap-2 px-3 pb-3 pt-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submitText();
            }
          }}
          placeholder={placeholder ?? t("messages.placeholder")}
          maxLength={1000}
          // `min-w-0` so the field yields before the send button does. iOS
          // focus-zoom is stopped by the viewport meta in index.html.
          className="h-11 min-w-0 flex-1 rounded-full border border-[#E4E2DA] bg-[#FAFAF8] px-4 text-[14px] text-[#1A1A18] placeholder:text-[#A6A49C] focus:border-[#EF9F27] focus:outline-none"
        />
        <button
          type="button"
          onClick={submitText}
          disabled={sending || !text.trim()}
          aria-label={t("messages.send")}
          // 44px is the smallest reliable tap target, and a driver presses this
          // in gloves. The office keeps the 40px it had.
          className={`flex shrink-0 items-center justify-center rounded-full bg-[#B4711F] text-white transition-opacity disabled:opacity-40 ${
            touch ? "h-11 w-11" : "h-10 w-10"
          }`}
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
